import {
  KNOWLEDGE_DOC_BOX_INTERACTION,
  KNOWLEDGE_DOC_SELECT,
  useGeckoAnalytics,
} from 'features/analytics'
import { useChatbotAttachments } from 'features/chatbot'
import { ContextMenu } from 'features/knowledgeDocumentViewer/components/ContextMenu'
import { KnowledgeDocumentSidebar } from 'features/knowledgeDocumentViewer/components/sidebar/KnowledgeDocumentSidebar'
import ImageViewerWithOverlay from 'features/knowledgeDocumentViewer/components/viewer/ImageViewerWithOverlay'
import { ZoomControls } from 'features/knowledgeDocumentViewer/components/viewer/ZoomControls'
import { useKnowledgeDocumentDataContext } from 'features/knowledgeDocumentViewer/contexts/KnowledgeDocumentDataContext'
import {
  ActionMode,
  FilterOption,
  TooltipOption,
  useKnowledgeDocumentUXContext,
} from 'features/knowledgeDocumentViewer/contexts/KnowledgeDocumentUXContext'
import { useLinkedDocumentsNavigation } from 'features/knowledgeDocumentViewer/hooks/useLinkedDocumentsNavigation'
import { useOffPageConnectorDocuments } from 'features/knowledgeDocumentViewer/hooks/useOffPageConnectorDocuments'
import { usePageNavigation } from 'features/knowledgeDocumentViewer/hooks/usePageNavigation'
import { useRecentDocuments } from 'features/knowledgeDocumentViewer/hooks/useRecentDocuments'
import { useRolodexEntriesCachedById } from 'features/knowledgeDocumentViewer/hooks/useRolodexEntriesCachedById'
import {
  DocumentViewerPage,
  SearchHistoryItem,
} from 'features/knowledgeDocumentViewer/types'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import styled from 'styled-components'
import { DocumentViewerAssetType, SystemDocumentEntry } from 'types'

import { AdvancedSearchPage } from './advancedSearch/AdvancedSearchPage'
import { transformDocumentsToTableData } from './advancedSearch/DocumentTableColumns'
import { DocumentTabs } from './toolbar/DocumentTabs'
import { KnowledgeDocumentHeaderBar } from './toolbar/KnowledgeDocumentHeaderBar'
import { useBoxClickHandlers } from './viewer/hooks/useBoxClickHandlers'
import { useImageViewerLogic } from './viewer/hooks/useImageViewerLogic'
import { Box, ImageViewerHandle } from './viewer/types'

const EditorContainer = styled.div`
  width: 100%;
  height: 100vh;
  display: flex;
  flex-direction: column;
  background-color: ${(props) => props.theme.forecast.background1};
  overflow: hidden;
`

const ViewerAndSidebarContainer = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: row;
  flex: 1;
  min-height: 0;
  overflow: hidden;
  position: relative;
  background-color: ${(props) => props.theme.forecast.pageBackground1};
`

const LoadingContainer = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.875rem;
  color: ${(props) => props.theme.forecast.secondaryText};
`

const ViewerContainer = styled.div`
  flex: 1;
  height: 100%;
  min-height: 0;
  min-width: 0;
  position: relative;
  background-color: ${(props) => props.theme.forecast.cardGroupBackground};
`

const SidebarContainer = styled.div<{ $isCollapsed: boolean }>`
  width: ${({ $isCollapsed }) => ($isCollapsed ? '0' : '23.5rem')};
  height: 100%;
  min-height: 0;
  flex-shrink: 0;
  overflow: hidden;
  transition: width 0.3s ease;
  display: ${({ $isCollapsed }) => ($isCollapsed ? 'none' : 'block')};
`

const TabsAndNavigatorContainer = styled.div`
  display: flex;
  align-items: center;
  background-color: ${(props) => props.theme.forecast.pageBackground1};
`

const TabsWrapper = styled.div`
  flex: 1;
  overflow: hidden;
`

export function KnowledgeDocumentTools({
  scale = 2,
}: {
  scale?: number
  fitOnLoad?: boolean
}) {
  const {
    documentSignedURL,
    totalPages,
    siteID,
    advancedSearchTableData,
    isLoadingDocuments,
  } = useKnowledgeDocumentDataContext()
  const { recentDocuments, addRecentDocument } = useRecentDocuments(siteID)
  const [boxContextMenuPosition, setBoxContextMenuPosition] = useState<{
    x: number
    y: number
  } | null>(null)
  const [contextMenuBox, setContextMenuBox] = useState<Box | null>(null)

  const {
    activeTabId,
    openTabs,
    setActiveTabId,
    tooltipOptions,
    setTooltipOptions,
    handleSelectDocument,
    addDocumentTab,
    actionMode,
    isFullscreen,
    isSidebarCollapsed,
    filterOptions,
    setFilterOptions,
    updateDocumentHistoryItem,
    updateSearchHistoryItem,
    isOnDocument,
    isOnLinkedDocuments,
    currentLinkedDocumentsData,
    currentDocumentId,
    currentPageNumber,
    currentSelectedBoxIds,
    currentSearchState,
  } = useKnowledgeDocumentUXContext()

  const [hoveredBoxLabel, setHoveredBoxLabel] = useState<string | undefined>()

  const {
    systemDocumentEntries: offPageConnectorDocuments,
    isLoading: isLoadingOffPageConnector,
  } = useOffPageConnectorDocuments(hoveredBoxLabel, siteID)

  const linkedDocIds = currentLinkedDocumentsData?.documentIds ?? []
  const { data: linkedDocs } =
    useRolodexEntriesCachedById<SystemDocumentEntry>(linkedDocIds)

  const { data: attachmentDocs } =
    useRolodexEntriesCachedById<SystemDocumentEntry>(
      currentDocumentId ? [currentDocumentId] : []
    )

  const handleBoxHover = useCallback((box: Box | null) => {
    setHoveredBoxLabel(
      box?.type === DocumentViewerAssetType.OFF_PAGE_CONNECTOR
        ? box.label
        : undefined
    )
  }, [])

  const { addAttachment } = useChatbotAttachments()

  const { trackEvent } = useGeckoAnalytics()
  const viewerRef = useRef<ImageViewerHandle>(null)

  const { navigateToLinkedDocuments } = useLinkedDocumentsNavigation({
    offPageConnectorDocuments,
    isLoadingOffPageConnector,
  })

  const {
    imageUrl: imageUrlLeftPage,
    boxes: boxesLeftPage,
    isLoading: isLoadingLeftPage,
  } = useImageViewerLogic(
    documentSignedURL?.signed_url ?? '',
    currentPageNumber,
    scale,
    DocumentViewerPage.LEFT_PAGE,
    totalPages
  )

  const {
    imageUrl: imageUrlRightPage,
    boxes: boxesRightPage,
    isLoading: isLoadingRightPage,
  } = useImageViewerLogic(
    documentSignedURL?.signed_url ?? '',
    currentPageNumber + 1,
    scale,
    DocumentViewerPage.RIGHT_PAGE,
    totalPages
  )

  const autoOpenNewTab = tooltipOptions.includes(TooltipOption.AutoOpenNewTab)

  const boxToOcrResult = useCallback(
    (box: Box, event: React.MouseEvent) => {
      const parts = box.id.split('-')
      const pageIndex = parseInt(parts[parts.length - 1], 10)
      const originalId = parts.slice(0, -1).join('-')

      const pageNumber = currentPageNumber + pageIndex

      return {
        id: parseInt(originalId, 10),
        text: box.label ?? '',
        type: box.type,
        bounding_box: {
          left: 0,
          top: 0,
          width: 0,
          height: 0,
        },
        pageNumber,
        confirmed: box.confirmed,
        forceNewTab: event.metaKey || event.ctrlKey || autoOpenNewTab,
      }
    },
    [currentPageNumber, autoOpenNewTab]
  )

  const handleBoxClickAction = useCallback(
    (box: Box, event: React.MouseEvent) => {
      const ocrResult = boxToOcrResult(box, event)

      trackEvent(KNOWLEDGE_DOC_BOX_INTERACTION.title, {
        interaction_type: 'box_click_linked_docs',
        box_id: box.id,
        box_type: box.type,
        document_id: currentDocumentId,
        page_number: currentPageNumber,
      })

      updateDocumentHistoryItem(activeTabId, { selectedBoxIds: [box.id] })

      const navigated = navigateToLinkedDocuments({
        ocrResult,
        forceNewTab: ocrResult.forceNewTab,
      })

      return navigated
    },
    [
      activeTabId,
      boxToOcrResult,
      currentDocumentId,
      currentPageNumber,
      navigateToLinkedDocuments,
      trackEvent,
      updateDocumentHistoryItem,
    ]
  )

  const boxClickHandlers = useBoxClickHandlers({
    selectedBoxIds: currentSelectedBoxIds,
    setSelectedBoxIds: (selectedBoxIds: string[]) => {
      updateDocumentHistoryItem(activeTabId, { selectedBoxIds })
    },
    onBoxClickAction: handleBoxClickAction,
  })

  const multiPageTurner = tooltipOptions.includes(TooltipOption.MultiPageTurner)

  usePageNavigation({
    currentPageNumber: currentPageNumber,
    totalPages,
    onPageChange: (pageNumber: number) => {
      updateDocumentHistoryItem(activeTabId, { pageNumber, selectedBoxIds: [] })
    },
    pageStep: multiPageTurner ? 2 : 1,
  })

  const onBoxClick = useCallback(
    (box: Box, event: React.MouseEvent) => {
      trackEvent(KNOWLEDGE_DOC_BOX_INTERACTION.title, {
        interaction_type: 'box_click',
        box_id: box.id,
        box_type: box.type,
        action_mode: actionMode,
        document_id: currentDocumentId,
        page_number: currentPageNumber,
      })
      const handler = boxClickHandlers[actionMode]
      if (handler) {
        handler(box, event)
      }
    },
    [
      actionMode,
      boxClickHandlers,
      trackEvent,
      currentDocumentId,
      currentPageNumber,
    ]
  )

  const onBoxAuxClick = useCallback(
    (box: Box, event: React.MouseEvent) => {
      if (event.button === 1 && actionMode === ActionMode.Info) {
        event.preventDefault()
        if (box.type === DocumentViewerAssetType.OFF_PAGE_CONNECTOR) {
          const ocrResult = boxToOcrResult(box, event)
          navigateToLinkedDocuments({ ocrResult, forceNewTab: true })
        }
      }
    },
    [actionMode, boxToOcrResult, navigateToLinkedDocuments]
  )

  const onBoxContextMenu = useCallback(
    (box: Box, event: React.MouseEvent) => {
      if (actionMode === ActionMode.Info) {
        event.preventDefault()
        if (box.type === DocumentViewerAssetType.OFF_PAGE_CONNECTOR) {
          setContextMenuBox(box)
          setBoxContextMenuPosition({ x: event.clientX, y: event.clientY })
        }
      }
    },
    [actionMode]
  )

  const handleBoxContextMenuClose = useCallback(() => {
    setBoxContextMenuPosition(null)
    setContextMenuBox(null)
  }, [])

  const boxToOcrResultWithForceNewTab = useCallback(
    (box: Box, forceNewTab: boolean) => {
      const parts = box.id.split('-')
      const pageIndex = parseInt(parts[parts.length - 1], 10)
      const originalId = parts.slice(0, -1).join('-')
      const pageNumber = currentPageNumber + pageIndex

      return {
        id: parseInt(originalId, 10),
        text: box.label ?? '',
        type: box.type,
        bounding_box: { left: 0, top: 0, width: 0, height: 0 },
        pageNumber,
        confirmed: box.confirmed,
        forceNewTab,
      }
    },
    [currentPageNumber]
  )

  const handleBoxContextMenuOpen = useCallback(() => {
    if (contextMenuBox) {
      const ocrResult = boxToOcrResultWithForceNewTab(contextMenuBox, false)
      navigateToLinkedDocuments({ ocrResult, forceNewTab: false })
    }
  }, [contextMenuBox, boxToOcrResultWithForceNewTab, navigateToLinkedDocuments])

  const handleBoxContextMenuOpenInNewTab = useCallback(() => {
    if (contextMenuBox) {
      const ocrResult = boxToOcrResultWithForceNewTab(contextMenuBox, true)
      navigateToLinkedDocuments({ ocrResult, forceNewTab: true })
    }
  }, [contextMenuBox, boxToOcrResultWithForceNewTab, navigateToLinkedDocuments])

  const onMultipleBoxSelect = useCallback(
    (boxIds: string[]) => {
      trackEvent(KNOWLEDGE_DOC_BOX_INTERACTION.title, {
        interaction_type: 'multi_select',
        selected_count: boxIds.length,
        action_mode: actionMode,
        document_id: currentDocumentId,
        page_number: currentPageNumber,
      })
      updateDocumentHistoryItem(activeTabId, {
        selectedBoxIds: [...currentSelectedBoxIds, ...boxIds],
      })
    },
    [
      activeTabId,
      currentDocumentId,
      currentPageNumber,
      currentSelectedBoxIds,
      updateDocumentHistoryItem,
      trackEvent,
      actionMode,
    ]
  )

  const handleZoomToBox = useCallback(
    (id: string) => {
      viewerRef.current?.zoomToBox(id, 2.5)
    },
    [viewerRef]
  )

  // Clear selection when clicking anywhere that isn't a box, table row, or interactive element
  const handlePageClick = useCallback(
    (event: React.MouseEvent) => {
      if (currentSelectedBoxIds.length === 0) return

      const target = event.target as HTMLElement

      const shouldPreserveSelection =
        target.tagName === 'rect' ||
        target.closest('tr') !== null ||
        target.closest('button') !== null ||
        target.closest('input') !== null ||
        target.closest('[data-preserve-selection]') !== null

      if (!shouldPreserveSelection) {
        updateDocumentHistoryItem(activeTabId, { selectedBoxIds: [] })
      }
    },
    [activeTabId, currentSelectedBoxIds, updateDocumentHistoryItem]
  )

  const handleAdvancedSearchSelect = useCallback(
    (documentId: string) => {
      trackEvent(KNOWLEDGE_DOC_SELECT.title, {
        document_id: documentId,
        source: 'advanced_search',
      })
      handleSelectDocument(documentId)
    },
    [handleSelectDocument, trackEvent]
  )

  const handleOpenInNewTab = useCallback(
    (documentId: string) => {
      trackEvent(KNOWLEDGE_DOC_SELECT.title, {
        document_id: documentId,
        source: 'advanced_search_new_tab',
      })
      addDocumentTab({ documentId })
    },
    [trackEvent, addDocumentTab]
  )

  const showMultiplePages = tooltipOptions.includes(
    TooltipOption.ShowMultiplePages
  )

  const hasSecondPage =
    showMultiplePages && totalPages > 1 && currentPageNumber < totalPages

  const combinedBoxes = useMemo(
    () =>
      showMultiplePages && totalPages > 1 && hasSecondPage
        ? [...boxesLeftPage, ...boxesRightPage]
        : boxesLeftPage,
    [
      showMultiplePages,
      totalPages,
      hasSecondPage,
      boxesLeftPage,
      boxesRightPage,
    ]
  )

  const combinedImageUrls =
    showMultiplePages &&
    totalPages > 1 &&
    hasSecondPage &&
    imageUrlRightPage &&
    imageUrlLeftPage
      ? [imageUrlLeftPage, imageUrlRightPage]
      : imageUrlLeftPage
        ? [imageUrlLeftPage]
        : undefined

  const linkedDocumentsTableData = currentLinkedDocumentsData
    ? transformDocumentsToTableData(linkedDocs ?? [])
    : null

  const handleSearchStateChange = useCallback(
    (updates: Partial<SearchHistoryItem['searchState']>) => {
      if (!isOnDocument) {
        updateSearchHistoryItem(activeTabId, updates)
      }
    },
    [activeTabId, isOnDocument, updateSearchHistoryItem]
  )

  const [isAtMinZoom, setIsAtMinZoom] = useState(false)
  const [isAtMaxZoom, setIsAtMaxZoom] = useState(false)

  const handleZoomStateChange = useCallback(
    (state: { isAtMinZoom: boolean; isAtMaxZoom: boolean }) => {
      setIsAtMinZoom(state.isAtMinZoom)
      setIsAtMaxZoom(state.isAtMaxZoom)
    },
    []
  )

  const handleZoomOut = useCallback(() => {
    viewerRef.current?.zoomOut()
  }, [])

  const handleZoomIn = useCallback(() => {
    viewerRef.current?.zoomIn()
  }, [])

  const handleResetZoom = useCallback(() => {
    viewerRef.current?.resetZoom()
  }, [viewerRef])

  const previousFullscreenState = useRef(isFullscreen)
  const fullscreenResetTimeoutRef = useRef<NodeJS.Timeout | null>(null)

  useEffect(() => {
    if (previousFullscreenState.current === isFullscreen) {
      return
    }

    previousFullscreenState.current = isFullscreen
    if (fullscreenResetTimeoutRef.current) {
      clearTimeout(fullscreenResetTimeoutRef.current)
    }

    fullscreenResetTimeoutRef.current = setTimeout(() => {
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          handleResetZoom()
        })
      })
    }, 100)

    return () => {
      if (fullscreenResetTimeoutRef.current) {
        clearTimeout(fullscreenResetTimeoutRef.current)
      }
    }
  }, [isFullscreen, handleResetZoom])

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      const activeElement = document.activeElement
      if (
        activeElement instanceof HTMLInputElement ||
        activeElement instanceof HTMLTextAreaElement
      ) {
        return
      }

      if (e.key === 'b' || e.key === 'B') {
        e.preventDefault()
        const newFilter =
          filterOptions === FilterOption.ShowConfirmed
            ? FilterOption.RemoveBoxes
            : FilterOption.ShowConfirmed
        setFilterOptions(newFilter)
      } else if (e.key === 't' || e.key === 'T') {
        e.preventDefault()
        const showingTooltips = tooltipOptions.includes(
          TooltipOption.ShowTooltips
        )
        const boxesHidden = filterOptions === FilterOption.RemoveBoxes
        if (!boxesHidden) {
          const newOptions = showingTooltips
            ? tooltipOptions.filter((opt) => opt !== TooltipOption.ShowTooltips)
            : [...tooltipOptions, TooltipOption.ShowTooltips]
          setTooltipOptions(newOptions)
        }
      }
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [filterOptions, setFilterOptions, tooltipOptions, setTooltipOptions])

  useEffect(() => {
    const selectedDocument = attachmentDocs?.[0]
    if (!currentDocumentId || !selectedDocument) {
      return
    }

    const filename = selectedDocument.entry.data?.filename ?? currentDocumentId

    const displayTitle =
      selectedDocument.entry.data?.document_title ??
      selectedDocument.entry.data?.title ??
      filename

    addAttachment({
      id: currentDocumentId,
      type: 'system_document',
      name: filename,
      displayTitle,
    })
  }, [currentDocumentId, attachmentDocs, addAttachment])

  return (
    <EditorContainer onClick={handlePageClick}>
      <TabsAndNavigatorContainer>
        <TabsWrapper>
          <DocumentTabs />
        </TabsWrapper>
      </TabsAndNavigatorContainer>
      {(currentDocumentId || isOnLinkedDocuments) && (
        <KnowledgeDocumentHeaderBar />
      )}
      <ViewerAndSidebarContainer>
        <ViewerContainer>
          {isOnLinkedDocuments && linkedDocumentsTableData ? (
            <AdvancedSearchPage
              onSelectDocument={handleAdvancedSearchSelect}
              onOpenNewTab={handleOpenInNewTab}
              advancedSearchTableData={linkedDocumentsTableData}
              siteId={siteID}
              isLoadingDocuments={false}
              searchState={currentSearchState}
              onSearchStateChange={handleSearchStateChange}
              recentDocuments={[]}
              onAddRecentDocument={addRecentDocument}
              openTabs={openTabs}
              onSwitchToTab={setActiveTabId}
              activeTabId={activeTabId}
              linkedDocumentIds={linkedDocIds}
            />
          ) : !isOnDocument ? (
            <AdvancedSearchPage
              onSelectDocument={handleAdvancedSearchSelect}
              onOpenNewTab={handleOpenInNewTab}
              advancedSearchTableData={advancedSearchTableData}
              siteId={siteID}
              isLoadingDocuments={isLoadingDocuments}
              searchState={currentSearchState}
              onSearchStateChange={handleSearchStateChange}
              recentDocuments={recentDocuments}
              onAddRecentDocument={addRecentDocument}
              openTabs={openTabs}
              onSwitchToTab={setActiveTabId}
              activeTabId={activeTabId}
            />
          ) : combinedImageUrls && combinedImageUrls.length > 0 ? (
            <>
              <ImageViewerWithOverlay
                ref={viewerRef}
                imageUrls={combinedImageUrls}
                boxes={combinedBoxes}
                highlightBoxIds={currentSelectedBoxIds}
                onBoxClick={onBoxClick}
                onBoxAuxClick={onBoxAuxClick}
                onBoxContextMenu={onBoxContextMenu}
                onBoxHover={handleBoxHover}
                onMultipleBoxSelect={onMultipleBoxSelect}
                actionMode={actionMode}
                showTooltips={tooltipOptions.includes(
                  TooltipOption.ShowTooltips
                )}
                isLoading={isLoadingLeftPage || isLoadingRightPage}
                onZoomStateChange={handleZoomStateChange}
              />
              <ZoomControls
                onZoomIn={handleZoomIn}
                onZoomOut={handleZoomOut}
                onResetZoom={handleResetZoom}
                disabled={
                  !currentDocumentId ||
                  !combinedImageUrls ||
                  combinedImageUrls.length === 0
                }
                zoomOutDisabled={isAtMinZoom}
                zoomInDisabled={isAtMaxZoom}
              />
            </>
          ) : (
            <LoadingContainer>Loading document...</LoadingContainer>
          )}
        </ViewerContainer>

        {isOnDocument && (
          <SidebarContainer $isCollapsed={isSidebarCollapsed}>
            <KnowledgeDocumentSidebar
              handleResetZoom={handleResetZoom}
              handleZoomToBox={handleZoomToBox}
            />
          </SidebarContainer>
        )}
      </ViewerAndSidebarContainer>
      <ContextMenu
        position={boxContextMenuPosition}
        onClose={handleBoxContextMenuClose}
        onOpen={handleBoxContextMenuOpen}
        onOpenInNewTab={handleBoxContextMenuOpenInNewTab}
      />
    </EditorContainer>
  )
}
